import React, { useState } from "react";
import {
  Typography,
  Box,
  Stack,
  Paper,
  Slider,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  calculateTotalWithPaymentsPro,
  PAYMENTS_PRO_MONTHLY,
} from "../utils/slotPricing";

const cardSx = {
  bgcolor: "#fff",
  borderRadius: 2,
  borderColor: "rgba(0,0,0,0.08)",
  boxShadow:
    "0 1px 2px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.06)",
};

const PricingCalculator = ({ embedded = false }) => {
  const [slots, setSlots] = useState(20);
  const [paymentsPro, setPaymentsPro] = useState(false);
  const { breakdown, total, paymentsProCost } = calculateTotalWithPaymentsPro(
    slots,
    paymentsPro
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: embedded ? { xs: 2.5, md: 3 } : 4,
        mb: embedded ? 0 : 6,
        height: embedded ? "100%" : undefined,
        textAlign: "left",
        ...cardSx,
      }}
    >
      <Typography
        variant="h5"
        component="h3"
        sx={{
          fontWeight: 600,
          mb: 1,
          fontSize: embedded
            ? { xs: "1.15rem", sm: "1.35rem" }
            : { xs: "1.25rem", sm: "1.5rem" },
        }}
      >
        Calculate your cost
      </Typography>
      <Typography
        variant="body1"
        sx={{
          mb: embedded ? 2 : 3,
          color: "text.secondary",
          fontSize: embedded ? "0.9rem" : undefined,
        }}
      >
        Select your total number of client slots to see how they break down by
        pricing tier. New coaches get a one-month free trial before billing
        starts.
      </Typography>
      <Box sx={{ mb: embedded ? 2 : 3 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Client slots: <strong>{slots}</strong>
        </Typography>
        <Slider
          value={slots}
          onChange={(_, value) => setSlots(value)}
          min={1}
          max={150}
          valueLabelDisplay="auto"
          marks={[
            { value: 10, label: "10" },
            { value: 30, label: "30" },
            { value: 60, label: "60" },
            { value: 100, label: "100" },
            { value: 150, label: "150" },
          ]}
          sx={{ maxWidth: embedded ? "100%" : 400 }}
        />
      </Box>
      <FormControlLabel
        sx={{
          mb: embedded ? 2 : 3,
          alignItems: "flex-start",
          ml: 0,
          "& .MuiFormControlLabel-label": { pt: 0.5 },
        }}
        control={
          <Switch
            checked={paymentsPro}
            onChange={(e) => setPaymentsPro(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Box>
            <Typography
              variant="body2"
              component="span"
              sx={{ fontWeight: 600 }}
            >
              Payments Pro
            </Typography>
            <Typography variant="body2" color="text.secondary" display="block">
              Stripe billing & payments — ${PAYMENTS_PRO_MONTHLY}/month flat
              (optional)
            </Typography>
          </Box>
        }
      />
      <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
          Calculation by slot range:
        </Typography>
        <Stack spacing={1}>
          {breakdown.map((row) => (
            <Typography
              key={row.range}
              variant="body2"
              sx={{ fontFamily: "monospace" }}
            >
              {row.slots} slots in {row.range} × ${row.pricePerSlot} = $
              {row.cost.toFixed(0)}
            </Typography>
          ))}
        </Stack>
        {paymentsProCost > 0 ? (
          <Typography variant="body2" sx={{ fontFamily: "monospace", mt: 1.5 }}>
            Payments Pro add-on: ${paymentsProCost}/month
          </Typography>
        ) : null}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            mt: 2,
            pt: 2,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          Total: ${total}/month
        </Typography>
      </Box>
    </Paper>
  );
};

export default PricingCalculator;
